import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ShoppingBag, Download, Package, Mail } from "lucide-react";
import SectionEyebrow from "@/components/site/SectionEyebrow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ProductCard({ product, onBuy }) {
    const sizes = product.sizes || [];
    const colors = product.colors || [];
    const [size, setSize] = useState(sizes[Math.min(1, sizes.length - 1)] || null);
    const [color, setColor] = useState(colors[0] || null);
    const [loading, setLoading] = useState(false);
    const isApparel = product.category === "T-Shirt" || product.category === "Sweatshirt";

    const buy = async (e) => {
        e.preventDefault();
        const form = e.target;
        const email = form.email.value;
        if (!email) {
            toast.error("Please enter your email.");
            return;
        }
        if (isApparel && sizes.length && !size) {
            toast.error("Please select a size.");
            return;
        }
        setLoading(true);
        await onBuy(product, { quantity: 1, size: isApparel ? size : null, color: colors.length ? color : null, customer_email: email });
        setLoading(false);
    };

    const Icon = product.type === "digital" ? Download : Package;

    return (
        <article
            className="bg-[#fbf9f8] border border-[#e3c7b9] rounded-sm overflow-hidden flex flex-col"
            data-testid={`product-${product.id}`}
        >
            <div className="relative aspect-square bg-[#e3c7b9]/30 overflow-hidden">
                <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                <span className="absolute top-4 left-4 bg-[#0c0f3d] text-[#fbf9f8] text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                    {product.category}
                </span>
            </div>
            <div className="p-7 flex-1 flex flex-col">
                <h3 className="font-serif text-2xl text-[#0c0f3d] leading-tight">{product.name}</h3>
                <p className="font-serif italic text-base text-[#0c0f3d]/65 mt-1">{product.tagline}</p>
                <p className="font-sans text-sm text-[#0c0f3d]/75 mt-4 leading-[1.7] flex-1">{product.description}</p>
                <div className="flex items-center justify-between mt-6 mb-5">
                    <div className="flex items-baseline gap-2">
                        <span className="font-serif text-3xl text-[#0c0f3d]">${product.price.toFixed(0)}</span>
                        <span className="font-sans text-xs text-[#0c0f3d]/50">USD</span>
                    </div>
                    <Icon size={16} className="text-[#e47a6a]" strokeWidth={1.5} />
                </div>

                {colors.length > 0 && (
                    <div className="mb-4">
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Color</label>
                        <div className="flex flex-wrap gap-1.5">
                            {colors.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setColor(c)}
                                    data-testid={`product-${product.id}-color-${c}`}
                                    className={`px-3 h-9 text-xs font-medium border transition-colors ${
                                        color === c
                                            ? "bg-[#0c0f3d] text-[#fbf9f8] border-[#0c0f3d]"
                                            : "bg-transparent text-[#0c0f3d] border-[#0c0f3d]/30 hover:border-[#0c0f3d]"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {isApparel && sizes.length > 0 && (
                    <div className="mb-4">
                        <label className="font-sans text-[10px] uppercase tracking-[0.25em] text-[#6d91ad] block mb-2">Size</label>
                        <div className="flex flex-wrap gap-1.5">
                            {sizes.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setSize(s)}
                                    data-testid={`product-${product.id}-size-${s}`}
                                    className={`w-9 h-9 text-xs font-medium border transition-colors ${
                                        size === s
                                            ? "bg-[#0c0f3d] text-[#fbf9f8] border-[#0c0f3d]"
                                            : "bg-transparent text-[#0c0f3d] border-[#0c0f3d]/30 hover:border-[#0c0f3d]"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={buy} className="mt-auto space-y-3">
                    <input
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        data-testid={`product-${product.id}-email`}
                        className="w-full font-sans text-sm bg-transparent border-b border-[#0c0f3d]/30 py-2.5 outline-none focus:border-[#e47a6a] text-[#0c0f3d] placeholder:text-[#0c0f3d]/40"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid={`product-${product.id}-buy-btn`}
                        className="group w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-[#0c0f3d] text-[#fbf9f8] text-xs uppercase tracking-[0.2em] hover:bg-[#e47a6a] transition-colors duration-300 disabled:opacity-50"
                    >
                        {loading ? "Redirecting…" : "Buy Now"}
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                </form>
            </div>
        </article>
    );
}

export default function Shop() {
    const [products, setProducts] = useState([]);
    const [filter, setFilter] = useState("All");

    useEffect(() => {
        axios.get(`${API}/shop/products?brand=tcg`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
    }, []);

    const buy = async (product, { quantity, size, color, customer_email }) => {
        try {
            const { data } = await axios.post(`${API}/shop/checkout`, {
                product_id: product.id,
                quantity,
                size,
                color,
                customer_email,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (err) {
            const msg = err?.response?.data?.detail || "Checkout unavailable.";
            toast.error(typeof msg === "string" ? msg : "Checkout failed.");
        }
    };

    const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
    const visible = filter === "All" ? products : products.filter((p) => p.category === filter);

    return (
        <main className="bg-[#fbf9f8] min-h-screen" data-testid="shop-page">
            {/* HERO */}
            <section className="pt-36 pb-12 md:pt-44 md:pb-16 bg-[#fbf9f8]">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-8">
                            <SectionEyebrow>The Shop</SectionEyebrow>
                            <h1 className="font-serif text-5xl md:text-7xl lg:text-[96px] leading-[0.92] tracking-tight text-[#0c0f3d]">
                                Carry the work
                                <span className="block italic text-[#e47a6a]">with you.</span>
                            </h1>
                        </div>
                        <div className="lg:col-span-4">
                            <p className="font-sans text-base md:text-lg text-[#0c0f3d]/75 leading-relaxed">
                                Tools, planners, and wearable reminders for anyone building a connection&#8209;centered life.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Filter strip */}
            <section className="border-y border-[#e3c7b9] bg-[#e3c7b9]/30 py-5">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-wrap items-center gap-2">
                    <span className="font-sans text-xs uppercase tracking-[0.25em] text-[#6d91ad] mr-3">Browse</span>
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setFilter(c)}
                            data-testid={`shop-filter-${c.toLowerCase().replace(" ", "-")}`}
                            className={`px-4 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors ${
                                filter === c
                                    ? "bg-[#0c0f3d] text-[#fbf9f8]"
                                    : "bg-transparent text-[#0c0f3d] border border-[#0c0f3d]/20 hover:border-[#0c0f3d]"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            {/* Grid */}
            <section className="py-16 md:py-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    {visible.length === 0 ? (
                        <div className="text-center py-16">
                            <ShoppingBag size={28} className="text-[#e47a6a] mx-auto mb-4" />
                            <p className="font-serif italic text-xl text-[#0c0f3d]">Stocking the shelves…</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                            {visible.map((p) => (
                                <ProductCard key={p.id} product={p} onBuy={buy} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Note */}
            <section className="border-t border-[#e3c7b9] bg-[#e3c7b9]/30 py-10">
                <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
                    <Mail size={20} className="text-[#e47a6a] mx-auto mb-3" />
                    <p className="font-sans text-sm text-[#0c0f3d]/75 leading-[1.8]">
                        Questions about an order? Email <a href="mailto:shana@theconnectionguru.co" className="underline decoration-[#e47a6a] decoration-2 underline-offset-4">shana@theconnectionguru.co</a> and we&apos;ll be in touch within 1&#8209;2 business days. Physical items ship via USPS within 3&#8209;5 days of order.
                    </p>
                </div>
            </section>
        </main>
    );
}
